import React from 'react';
import styled from 'styled-components';
import { getStatusColor, Message, ModalText, Status, Tag } from 'components';
import { Align, Appearance, lineHeight, Size, theme, toRem } from 'design';
import { IPayout } from 'types';
import { centsToDollarsDisplay } from 'utils/currency';
import { formatDateAbbreviated } from 'utils/date';
import { capitalize } from 'utils/strings';

// Keyed by layout. converting and reviewRequired always have payouts, so noPayouts only stands in for confirm. Arrays render as a bulleted notice.
const DESCRIPTION = {
  converting: ['The payments below will be converted to payroll payments.', "Once this pay period is closed, Kickfin won't pay these. You'll need to include them in payroll."],
  confirm: ["Once this pay period is closed, you can't issue new payments or cancel pending ones.", 'Kickfin will keep trying to deliver pending payments.', 'You can still retry expired payments.'],
  noPayouts: 'Closing this pay period will prevent you from issuing new payments.',
  reviewRequired: ['These payments require management review before you can close this pay period.', 'A reviewing manager must approve or cancel each one.'],
};

// Bulleted copy above the table.
const Notice = styled(Message)`
  margin-top: ${toRem(theme.space.md)};
  padding: ${toRem(theme.space.md)} ${toRem(theme.space.lg)};

  ul {
    padding-left: ${toRem(theme.space.lg)};
  }
`;

// Rows scroll under the pinned header. Margin keeps the top gap out of the scroll; padding keeps the scrollbar off the last column.
const ScrollableList = styled.div`
  display: block;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  margin-top: ${toRem(theme.space.xl)};
  padding-right: ${toRem(theme.space.md)};
  overflow-y: auto;
`;

const PayoutsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${theme.font.family.euclid};
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
  color: ${theme.color.neutral[800]};
  text-align: left;

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 0 ${toRem(theme.space['2xl'])} ${toRem(theme.space.sm)} 0;
    background-color: ${theme.color.white};
    font-weight: ${theme.font.weight.medium};
    white-space: nowrap;
  }

  td {
    padding: ${toRem(theme.space.md)} ${toRem(theme.space['2xl'])} ${toRem(theme.space.sm)} 0;
    white-space: nowrap;
    vertical-align: top;
  }

  td:first-child {
    min-width: ${toRem(120)};
    white-space: normal;
    word-break: break-word;
  }

  th:last-child,
  td:last-child {
    padding-right: 0;
  }
`;

// The table's text size, so the label lines up with the other cells.
const TableStatus = styled(Status)`
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
`;

const ReviewRequiredTag = styled(Tag)`
  padding: ${toRem(theme.space['2xs'])} ${toRem(theme.space.sm)};
  color: ${theme.color.warning[100]};
`;

interface IConfirmStepProps {
  // The undelivered instant payouts, or those awaiting review when any are.
  payouts: IPayout[];
  // confirm leaves the listed payouts in flight, converting turns them to payroll, reviewRequired lists those blocking the close.
  layout: 'confirm' | 'converting' | 'reviewRequired';
  dataCy?: string;
}

// Step 2: the copy for the close, and its payouts when there are any.
const ConfirmStep = ({ payouts, layout, dataCy = 'close-pay-period-payouts-list' }: IConfirmStepProps) => {
  const description = DESCRIPTION[payouts.length ? layout : 'noPayouts'];
  return (
    <>
      {Array.isArray(description) ? (
        <Notice
          dataCy="close-pay-period-confirm-description"
          appearance={Appearance.INFO}
          alignMessage={Align.LEFT}
          message={
            <ul>
              {description.map((line, idx) => (
                <li key={idx}>{line}</li>
              ))}
            </ul>
          }
        />
      ) : (
        <ModalText data-cy="close-pay-period-confirm-description">{description}</ModalText>
      )}
      {payouts.length > 0 && (
        <ScrollableList data-cy={dataCy}>
          <PayoutsTable>
            <thead>
              <tr>
                <th>Name</th>
                <th>Payment ID</th>
                <th>Business Day</th>
                <th>Amount</th>
                <th>Status</th>
                {layout === 'reviewRequired' && <th />}
              </tr>
            </thead>
            <tbody>
              {payouts.map((payout) => (
                <tr key={payout.id} data-cy="payout-row" data-value={payout.id}>
                  <td>{payout.employee?.name}</td>
                  <td>{payout.id}</td>
                  <td>{formatDateAbbreviated(payout.paymentDate)}</td>
                  <td>{centsToDollarsDisplay(payout.net)}</td>
                  <td>
                    <TableStatus appearance={getStatusColor(payout.status)} label={capitalize(payout.status)} />
                  </td>
                  {layout === 'reviewRequired' && (
                    <td>
                      <ReviewRequiredTag label="Review Required" appearance={Appearance.WARNING} size={Size.SMALL} dataCy="review-required-tag" />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </PayoutsTable>
        </ScrollableList>
      )}
    </>
  );
};

export default ConfirmStep;
